import {ref, watch} from 'vue';
import {useHandicapApi} from '~/composables/useHandicapApi';

// Per-competition handicap settings, fetched lazily and cached for the session — shared by
// every surface that needs LABELS (standings, ladders, team lists, team cards). A competition
// response says `handicapsEnabled`; the rendered option lists live on the settings object.
const cache = new Map();

/**
 * @param scopeRef ref/computed → {ladderId|leagueId|tournamentId|americanoId} | null
 * @param enabledRef ref/computed → boolean (skip the request when the competition says off)
 */
export function useHandicapSettingsFor(scopeRef, enabledRef) {
  const {getSettings} = useHandicapApi();
  const settings = ref(null);

  const keyOf = (scope) => scope ? Object.entries(scope).filter(([, v]) => v).map(([k, v]) => `${k}=${v}`).join('&') : null;

  const load = async () => {
    const scope = scopeRef.value;
    const key = keyOf(scope);
    if (!key || enabledRef?.value === false) {
      settings.value = null;
      return;
    }
    if (cache.has(key)) {
      settings.value = await cache.get(key);
      return;
    }
    const p = getSettings(scope).catch(() => null);
    cache.set(key, p);
    settings.value = await p;
  };

  const invalidate = () => {
    const key = keyOf(scopeRef.value);
    if (key) cache.delete(key);
    return load();
  };

  watch([() => keyOf(scopeRef.value), () => enabledRef?.value], load, {immediate: true});
  return {settings, reload: invalidate};
}
